import React, { useEffect, useState } from 'react'
import { useForm, SubmitHandler, Controller } from 'react-hook-form'
import PhoneInput from 'react-phone-number-input'
import Input from '@components/Input'
import { yupResolver } from '@hookform/resolvers/yup'
import { Button } from '@components/Button/Button'
import { useTranslation } from 'react-i18next'
import useCommonStyles from './InitialStep.style'
import FormError from '@components/FormError'
import { useChoiceState } from '@context/ChoiceContext'
import { usePhoneValidationSchema } from '@hooks/usePhoneValidationSchema'
import { GapFillerSurvey, postGapFillerSurvey } from 'src/services/api/gapFiller'
import Page from '@components/Page'
import Footer from '@components/Footer'
import { getErrorTranslationKey } from 'src/helpers/getApiErrorTranslationKey'
import logToPostHog from 'src/helpers/logToPosthog'
import { useConfig } from '@context/ConfigurationContext'
import { PatientTypeOptions } from 'src/config/patientTypeOptions'
import { SURVEY_PHONE_ENTRY } from 'src/config/imageUrls'
import clsx from 'clsx'
import { Alert } from '@mui/material'
import { useCloseAvailyContext } from '@context/CloseAvailyContext'
import Header from '@components/Header'
import { useSurveyContext } from '@context/SurveyContext'
import captureExceptionWithResponse from 'src/services/sentry'

interface PhoneEntryStepProps {
  onNext: () => void
  postHogAction: string
}

interface FormData {
  phone: string
}

const phoneNotEmptyLength = 4

const InitialStep = ({ onNext, postHogAction }: PhoneEntryStepProps) => {
  const { t } = useTranslation()
  const classes = useCommonStyles()
  const { patientType, treatmentBundleTypeReferenceId, insuranceType, validatedPhoneNumber, verifiedPatientData } =
    useChoiceState()
  const [error, setError] = useState('')
  const [inputFocused, setInputFocused] = useState(false)
  const [phoneNumberLength, setPhoneNumberLength] = useState(0)
  const phoneValidationSchema = usePhoneValidationSchema()
  const closeAvaily = useCloseAvailyContext()
  const { clinicId } = useConfig()
  const { phoneNumber: initialPhoneNumber } = useSurveyContext()

  const {
    control,
    handleSubmit,
    formState: { errors },
    watch,
  } = useForm<FormData>({
    resolver: yupResolver(phoneValidationSchema),
    defaultValues: { phone: validatedPhoneNumber ?? initialPhoneNumber },
  })

  useEffect(() => {
    const subscription = watch((value) => setPhoneNumberLength(value.phone?.length || 0))

    return () => subscription.unsubscribe()
  }, [watch])

  const onClose = () => {
    logToPostHog('Gap filler runaway - closed')
    closeAvaily && closeAvaily()
  }

  const onSubmit: SubmitHandler<FormData> = async ({ phone }) => {
    const surveyData: GapFillerSurvey = {
      patientType: patientType || PatientTypeOptions.NEW,
      treatmentBundleReferenceId: treatmentBundleTypeReferenceId,
      phoneNumber: validatedPhoneNumber ? validatedPhoneNumber : phone,
      clinicReferenceId: clinicId,
      insuranceType,
      patientReferenceId: verifiedPatientData.patientReferenceId ?? undefined,
      preferredPractitionerReferenceId: verifiedPatientData.preferredPractitionerReferenceId ?? undefined,
    }

    try {
      await postGapFillerSurvey(surveyData)
      logToPostHog('Gap filler runaway - submit success')
      onNext()
    } catch (error: any) {
      logToPostHog('Gap filler runaway - submit fail')
      captureExceptionWithResponse('Gap Filler: Missing required params', error, surveyData)
      setError(getErrorTranslationKey(error))
    }
  }

  return (
    <Page
      header={
        <Header
          desktopHeadingText=""
          mobileHeadingText={t('survey_initial_step_mobileHeadingText')}
          mobileSubheadingText=""
          postHogAction={postHogAction}
          hideStatusBar
        />
      }
      body={
        <div className={classes.wrapper}>
          <div className={classes.contentWrapper}>
            <div className={classes.title}>{t('gap_filler_survey_title')}</div>
            <div className={classes.description}>{t('gap_filler_survey_description')}</div>
            <img className={classes.mobileImage} src={SURVEY_PHONE_ENTRY} alt="woman sitting on a calendar" />

            <form onSubmit={handleSubmit(onSubmit)} className={classes.form}>
              <div className={classes.phoneInputContainer}>
                <Controller
                  control={control}
                  name="phone"
                  defaultValue=""
                  render={({ field }) => (
                    <>
                      <PhoneInput
                        international
                        inputComponent={Input}
                        defaultCountry="DE"
                        country="DE"
                        placeholder={t('phone')}
                        value={field.value}
                        onChange={(value) => field.onChange(value)}
                        required
                        className={clsx('phoneInput', { [classes.phoneInputFocused]: inputFocused })}
                        onFocus={() => setInputFocused(true)}
                        onBlur={() => setInputFocused(false)}
                        disabled={!!validatedPhoneNumber}
                      />
                      {errors.phone && <FormError message={errors.phone.message} />}
                    </>
                  )}
                />
              </div>
              {error && <Alert severity="error">{t(error)}</Alert>}
            </form>
          </div>
          <div>
            <img className={classes.image} src={SURVEY_PHONE_ENTRY} alt="woman sitting on a calendar" />
          </div>
        </div>
      }
      footer={
        <Footer
          beforeButton={
            <Button
              disabled={
                !validatedPhoneNumber &&
                (initialPhoneNumber ? initialPhoneNumber.length : phoneNumberLength) < phoneNotEmptyLength
              }
              defaultWidth
              className={classes.mainCta}
              onClick={handleSubmit(onSubmit)}
              type="submit"
              fullSize
              text={t('gap_filler_survey_main_cta')}
            />
          }
          button={
            <Button variant="borderless" onClick={onClose} fullSize text={t('gap_filler_survey_secondary_cta')} />
          }
        />
      }
    />
  )
}

export default InitialStep
